// ═══════════════════════════════════════════════════════════════════════════
// AuthLayout — shared shell for /auth/* pages.
//
// Login and Register were duplicating the same outer chrome (centered
// gradient backdrop, card, padding, max-width). Extracting it here gives
// us a single place to evolve the auth aesthetic without touching every
// page, and removes ~30 lines of identical inline styles per consumer.
//
// Pages wrap their form-level content with <AuthLayout>...</AuthLayout>
// and keep the form-specific JSX inside. EmailVerification and password-
// reset pages can adopt the layout too via the `cardMaxWidth` /
// `cardPadding` / `background` props that let them shrink the card or
// soften the gradient without re-implementing the whole shell.
// ═══════════════════════════════════════════════════════════════════════════

import type { CSSProperties, ReactNode } from 'react';
import styles from './AuthLayout.module.css';

// Mirrors the `--auth-bg` token published by AuthLayout.module.css.
// Kept as a string so tests / Storybook can render the layout without
// the global stylesheet attached.
const DEFAULT_GRADIENT = 'var(--auth-bg)';

interface AuthLayoutProps {
  children: ReactNode;
  /** Max width of the inner card. Default 420px matches Login/Register. */
  cardMaxWidth?: number;
  /** Inner card padding. Default `40px` — pass `'24px'` for tighter pages. */
  cardPadding?: CSSProperties['padding'];
  /**
   * Outer wrapper background. Defaults to the brand gradient. Pass a
   * solid colour like `var(--bg)` for screens that want a calmer chrome
   * (the email-verification + password-reset pages currently do this
   * via their own wrappers; with this prop they can adopt AuthLayout).
   */
  background?: CSSProperties['background'];
  /** Optional className for the outer wrapper. */
  className?: string;
  /** id on the wrapper for testing / a11y / deep-linking. */
  id?: string;
}

export function AuthLayout({
  children,
  cardMaxWidth = 420,
  cardPadding = '40px',
  background = DEFAULT_GRADIENT,
  className,
  id,
}: AuthLayoutProps) {
  return (
    <div
      role="main"
      id={id}
      className={className ? `${styles.shell} ${className}` : styles.shell}
      style={{ background }}
    >
      <div
        className={styles.card}
        style={{ maxWidth: `${cardMaxWidth}px`, padding: cardPadding }}
      >
        {children}
      </div>
    </div>
  );
}

export default AuthLayout;
